import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styles from './Sidebar.module.css';

// Компактные inline-SVG иконки навигации (stroke наследует currentColor).
const ICONS: Record<string, ReactNode> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.2a3 3 0 0 1 0 5.6" />
      <path d="M17.5 20a5.5 5.5 0 0 0-2.3-4.5" />
    </>
  ),
  admins: (
    <>
      <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1" />
    </>
  ),
  logs: (
    <>
      <path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
      <path d="M8 9h7M8 13h7M8 17h4" />
    </>
  ),
  monitoring: (
    <>
      <path d="M3 13h4l2.5-6 4 12L16 13h5" />
      <circle cx="12" cy="12" r="9.2" />
    </>
  ),
};

const NAV_ITEMS = [
  { to: '/dashboard', key: 'dashboard' },
  { to: '/users', key: 'users' },
  { to: '/admins', key: 'admins' },
  { to: '/settings', key: 'settings' },
  { to: '/logs', key: 'logs' },
  // PARA-129, AC-14: новый пункт рядом с «Журналом».
  { to: '/monitoring', key: 'monitoring' },
] as const;

function NavIcon({ name }: { name: string }): JSX.Element {
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
}

// PARA-106 §3 (UI-архитектура) — навигация Dashboard/Users/Admins/Settings/Logs;
// PARA-129 добавляет Monitoring.
export function Sidebar(): JSX.Element {
  const { t } = useTranslation();

  return (
    <aside className={styles.sidebar}>
      <div className={styles.brand}>
        <span className={styles.brandMark} aria-hidden="true">
          P
        </span>
        <span className={styles.brandText}>
          <span className={styles.brandName}>Paraparapam</span>
          <span className={styles.brandRole}>Admin</span>
        </span>
      </div>
      <nav className={styles.nav} aria-label="main">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            className={({ isActive }) =>
              [styles.link, isActive ? styles.active : ''].filter(Boolean).join(' ')
            }
          >
            <NavIcon name={item.key} />
            {t(`nav.${item.key}`)}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
